'use client';

import { useEffect, useRef, useState } from 'react';
import {
  acceptedVideoMimeTypes,
  formatVideoDurationMs,
  useLoadedVideoSource,
} from '@/components/tools/video-tool-shared';
import { extractAudioFromVideo, type VideoAudioOutputFormat } from '@/lib/browser-video-tools';
import { clamp, formatFileSize, getBaseName } from '@/lib/browser-image-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

const bitrateOptions = [128, 192, 256, 320];

// The format-pair landing pages (mov-to-mp3, mov-to-wav, mp4-to-m4a and the rest)
// all run this same extractor; only the container they are named after and the
// format the picker starts on differ. Opening a page called "MOV to WAV" with MP3
// preselected makes the visitor do the tool's job for it, so the default is a prop.
export default function BrowserExtractAudioFromVideo({
  defaultFormat = 'mp3',
  heading,
  toolSlug,
}: {
  defaultFormat?: VideoAudioOutputFormat;
  /** Pair pages (flv-to-mp3 …) show their own heading and report usage under their slug. */
  heading?: string;
  toolSlug?: string;
} = {}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedVideoSource();
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [startMs, setStartMs] = useState(0);
  const [endMs, setEndMs] = useState(0);
  const [format, setFormat] = useState<VideoAudioOutputFormat>(defaultFormat);
  const [bitrateKbps, setBitrateKbps] = useState(192);

  useEffect(() => {
    return () => {
      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }
    };
  }, [outputUrl]);

  useEffect(() => {
    if (!source) {
      return;
    }

    setStartMs(0);
    setEndMs(source.metadata.durationMs);
  }, [source]);

  function resetOutputState() {
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
    }

    setOutputUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The video could not be loaded.');
    }
  }

  async function runExtraction() {
    if (!source) {
      setErrorMessage('Choose a video first.');
      return;
    }

    if (endMs <= startMs) {
      setErrorMessage('Set an end time that is later than the start time.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        toolSlug ?? 'extract-audio-from-video',
        'Credits are not available for another audio extraction right now.',
      );
      const outputBlob = await extractAudioFromVideo({
        file: source.file,
        startMs,
        endMs,
        format,
        bitrateKbps,
      });
      const nextOutputUrl = URL.createObjectURL(outputBlob);

      resetOutputState();
      setOutputUrl(nextOutputUrl);
      setDownloadName(`${getBaseName(source.file.name)}-audio.${format}`);
      setResultSize(outputBlob.size);
      setCreditMessage(formatToolCreditSuccess('Audio extraction', credits));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The audio could not be extracted.');
    } finally {
      setIsProcessing(false);
    }
  }

  const outputMimeType =
    format === 'wav' ? 'audio/wav' : format === 'm4a' ? 'audio/mp4' : 'audio/mpeg';

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{heading ?? 'Extract audio from video'}</h2>
          <p>
            Pull the audio track from a video clip and export it as MP3, WAV, or M4A locally in your
            browser.
          </p>
        </div>
        <span className="tool-live-badge">Audio</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one video or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Extract audio-only output in MP3, WAV, or M4A'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={acceptedVideoMimeTypes}
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Start (ms)</span>
              <input
                min="0"
                onChange={(event) => {
                  setStartMs(Math.max(0, Number(event.target.value) || 0));
                  resetOutputState();
                }}
                type="number"
                value={startMs}
              />
            </label>
            <label className="tool-live-field">
              <span>End (ms)</span>
              <input
                min="0"
                onChange={(event) => {
                  setEndMs(Math.max(0, Number(event.target.value) || 0));
                  resetOutputState();
                }}
                type="number"
                value={endMs}
              />
            </label>
          </div>

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Output format</span>
              <select
                onChange={(event) => {
                  setFormat(event.target.value as VideoAudioOutputFormat);
                  resetOutputState();
                }}
                value={format}
              >
                <option value="mp3">MP3</option>
                <option value="wav">WAV</option>
                <option value="m4a">M4A</option>
                <option value="ogg">OGG</option>
              </select>
            </label>
            {format !== 'wav' ? (
              <label className="tool-live-field">
                <span>Bitrate</span>
                <select
                  onChange={(event) => {
                    setBitrateKbps(clamp(Number(event.target.value) || 192, 64, 320));
                    resetOutputState();
                  }}
                  value={bitrateKbps}
                >
                  {bitrateOptions.map((option) => (
                    <option key={option} value={option}>
                      {option} kbps
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runExtraction()}
              type="button"
            >
              {isProcessing ? 'Extracting…' : 'Extract audio'}
            </button>
            {outputUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={outputUrl}>
                Download audio
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Extraction details</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Source duration</strong>
              <span>
                {source ? formatVideoDurationMs(source.metadata.durationMs) : 'No video loaded'}
              </span>
            </div>
            <div>
              <strong>Clip length</strong>
              <span>
                {source ? formatVideoDurationMs(Math.max(0, endMs - startMs)) : 'No video loaded'}
              </span>
            </div>
            <div>
              <strong>Output format</strong>
              <span>{format.toUpperCase()}</span>
            </div>
            <div>
              <strong>Result size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'No export yet'}</span>
            </div>
          </div>

          <div className="tool-live-preview">
            <h3>Preview</h3>
            <div className="tool-live-preview-frame">
              {outputUrl ? (
                <audio controls src={outputUrl} />
              ) : source ? (
                <video controls playsInline src={source.previewUrl} />
              ) : (
                <p>Load a video to preview its source clip.</p>
              )}
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}
